import type { CycleManager, CycleManagerMoment } from '../models/CycleTypes';

export interface PendingEmployees {
	pendingEmployeeIDs: string[];
	pendingEmployeeNames: string[];
}

function getCompletedStatusKeys(moment: CycleManagerMoment): string[] {
	if (moment.goals) {
		return ['goalsCompleted'];
	}

	if (moment.message) {
		return ['messageRead'];
	}

	if (moment.feedback) {
		return ['feedbackGivers'];
	}

	const keys = moment.statuses.map((status) => status.key);

	if (keys.includes('managerConfirmed')) {
		return ['managerConfirmed'];
	}

	if (keys.includes('employeeConfirmed')) {
		return ['employeeConfirmed'];
	}

	if (keys.includes('managerShared')) {
		return ['managerShared'];
	}

	if (keys.includes('employeeShared')) {
		return ['employeeShared'];
	}

	if (keys.includes('completed')) {
		return ['completed'];
	}

	return [];
}

export function findPendingEmployees(manager: CycleManager, momentID: string): PendingEmployees {
	const moment = manager.moments.find((item) => item.id === momentID);

	if (!moment || moment.progress.total === 0) {
		return { pendingEmployeeIDs: [], pendingEmployeeNames: [] };
	}

	// Steps overlap (someone who reached a later step is also listed in earlier
	// ones), so pending means: not among those who reached the completed step.
	const completedKeys = new Set(getCompletedStatusKeys(moment));
	const completedIDs = new Set(
		moment.statuses
			.filter((status) => completedKeys.has(status.key))
			.flatMap((status) => status.members.map((member) => member.id)),
	);

	const pendingEmployees = moment.participants.filter((member) => !completedIDs.has(member.id));

	return {
		pendingEmployeeIDs: pendingEmployees.map((member) => member.id),
		pendingEmployeeNames: pendingEmployees
			.map((member) => `${member.firstName} ${member.lastName}`.trim())
			.filter(Boolean),
	};
}
